import { useEffect, useState, type JSX } from 'react';
import { ChevronDown, Megaphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useFolderGuidance, useSaveFolderGuidance, useClippingCampaigns } from '../hooks/folders';

/**
 * The admin's briefing for a folder, shown to outreachers next to that
 * folder's prospects in their queue.
 *
 * Free text on purpose. A structured campaign picklist was considered and
 * deferred — outreach has no campaign catalogue to reference (the only
 * campaign identifier anywhere is one env var used for payment routing), so a
 * dropdown would have meant inventing a list before knowing whether it should
 * mirror the clipping service's.
 *
 * Stored outreach-side rather than on the folder: outreachers read it there,
 * and denormalising it onto prospect rows (as the folder *name* already is)
 * would go stale the moment it's edited.
 */
const TEXTAREA_CLS =
  'min-h-[3rem] w-full resize-y rounded-lg border border-input bg-[var(--input-bg)] px-3 py-2 text-sm shadow-control placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25';

export function FolderGuidancePanel({
  folderPublicId,
  alwaysOpen = false,
}: {
  folderPublicId: string;
  /** The folder's Briefing tab shows the form straight away, with no fold. */
  alwaysOpen?: boolean;
}): JSX.Element {
  const { data, isLoading, error } = useFolderGuidance(folderPublicId);
  const saveMutation = useSaveFolderGuidance(folderPublicId);

  const [openState, setOpen] = useState(false);
  const open = alwaysOpen || openState;
  const [campaigns, setCampaigns] = useState('');
  const [campaignIds, setCampaignIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);

  // Re-seed the form when the folder changes or a fetch lands, but never
  // clobber edits in progress.
  useEffect(() => {
    if (dirty) return;
    setCampaigns(data?.recommendedCampaigns ?? '');
    setCampaignIds(data?.campaignIds ?? []);
    setNotes(data?.pitchNotes ?? '');
  }, [data, dirty]);

  useEffect(() => {
    setDirty(false);
    setOpen(false);
  }, [folderPublicId]);

  const hasGuidance = !!(
    data?.recommendedCampaigns ||
    data?.pitchNotes ||
    (data?.campaignIds ?? []).length > 0
  );

  function save() {
    saveMutation.mutate(
      {
        recommendedCampaigns: campaigns.trim() || null,
        campaignIds,
        pitchNotes: notes.trim() || null,
      },
      { onSuccess: () => setDirty(false) },
    );
  }

  // The bridge is optional; without OUTREACH_API_URL this endpoint 503s and
  // there's nothing useful to show.
  if (error) {
    return alwaysOpen ? (
      <p className="text-sm text-muted-foreground">
        The outreacher briefing isn't available right now — the outreach service didn't respond.
      </p>
    ) : (
      <></>
    );
  }

  return (
    <section
      className={cn(
        'rounded-xl',
        hasGuidance ? 'border border-brand/30 bg-brand/5' : 'panel',
      )}
    >
      <button
        type="button"
        onClick={() => !alwaysOpen && setOpen((v) => !v)}
        aria-expanded={open}
        disabled={alwaysOpen}
        className="flex w-full items-center gap-2.5 px-4 py-3 text-left disabled:cursor-default"
      >
        <Megaphone
          className={cn('h-4 w-4 shrink-0', hasGuidance ? 'text-primary' : 'text-muted-foreground')}
          strokeWidth={1.75}
        />
        <span className="text-sm font-semibold">Outreacher briefing</span>
        {!open && (
          <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
            {hasGuidance
              ? (data?.recommendedCampaigns ?? data?.pitchNotes)
              : 'Tell outreachers which campaigns suit these creators'}
          </span>
        )}
        {!alwaysOpen && (
          <ChevronDown
            className={cn(
              'ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-[120ms]',
              open && 'rotate-180',
            )}
            strokeWidth={2}
          />
        )}
      </button>

      {open && (
        <div className="space-y-4 border-t border-border/60 px-4 py-4">
          <p className="text-sm text-muted-foreground">
            Outreachers see this next to this folder's leads in their queue.
          </p>
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          <label className="grid gap-1">
            <span className="text-sm font-medium">Recommended campaigns</span>
            <textarea
              className={TEXTAREA_CLS}
              rows={2}
              placeholder="e.g. Love Island S12, Reality Recap — these editors do well on both"
              value={campaigns}
              maxLength={2000}
              onChange={(ev) => {
                setCampaigns(ev.target.value);
                setDirty(true);
              }}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-sm font-medium">Pitch notes</span>
            <textarea
              className={cn(TEXTAREA_CLS, 'min-h-[4rem]')}
              rows={3}
              placeholder="Anything an outreacher should know before writing the DM."
              value={notes}
              maxLength={4000}
              onChange={(ev) => {
                setNotes(ev.target.value);
                setDirty(true);
              }}
            />
          </label>
          <div className="flex flex-wrap items-center gap-3">
          {/* Structured campaign links, above the free-text note. These are
              what an outreacher can actually click and copy; the note below
              carries nuance a list of ids can't. */}
          <CampaignPicker selected={campaignIds} onChange={(next) => { setCampaignIds(next); setDirty(true); }} />

            <Button size="sm" onClick={save} disabled={saveMutation.isPending || !dirty}>
              {saveMutation.isPending ? 'Saving…' : 'Save briefing'}
            </Button>
            {data?.updatedByName && !dirty && (
              <span className="text-sm text-muted-foreground">
                Last edited by {data.updatedByName}
              </span>
            )}
            {saveMutation.error && (
              <span className="text-sm text-destructive-strong">
                {(saveMutation.error as Error).message}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Multi-select of live clipping campaigns.
 *
 * Only ids are saved. Names and links resolve live on read, so a renamed or
 * ended campaign never leaves a stale link where an outreacher could copy it
 * into a DM.
 */
function CampaignPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  const { data, isLoading } = useClippingCampaigns();
  const campaigns = data?.campaigns ?? [];

  if (isLoading) {
    return <p className="w-full text-sm text-muted-foreground">Loading campaigns…</p>;
  }
  // Distinguish "not wired up yet" from "nothing live" — they need different
  // actions from whoever is reading. `available` reflects whether clipping
  // answered, so an empty list from a healthy service falls through to the
  // second message rather than sending someone to chase a shipped endpoint.
  if (!data?.available) {
    return (
      <p className="w-full text-sm text-muted-foreground">
        Campaign linking is unavailable right now — the clipping service didn't respond. Use the note
        below in the meantime.
      </p>
    );
  }
  if (campaigns.length === 0) {
    return (
      <p className="w-full text-sm text-muted-foreground">
        No live or upcoming campaigns to link right now. Use the note below.
      </p>
    );
  }

  return (
    <div className="grid w-full gap-1.5">
      <span className="text-sm font-medium">Campaigns this folder is for</span>
      <div className="flex flex-wrap gap-1.5">
        {campaigns.map((c) => {
          const active = selected.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(active ? selected.filter((x) => x !== c.id) : [...selected, c.id])}
              title={c.description ?? undefined}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                active ? 'border-brand/50 bg-brand/15 text-foreground' : 'border-input text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {c.name}
              {c.status === 'pending' ? ' · soon' : ''}
            </button>
          );
        })}
      </div>
      <p className="text-sm text-muted-foreground">
        Outreachers see these on the folder brief and can copy each campaign's creator link.
      </p>
    </div>
  );
}
